/**
 * Sound Manager
 *
 * Manages game sound effects using ZzFX and background music
 * Provides separate mute toggles and localStorage persistence
 */

import { zzfx } from 'zzfx';
import { SettingsManager } from './SettingsManager';
import { GAME_CONSTANTS } from '../config/constants';

export type SoundType = 'jump' | 'land' | 'bounce' | 'slide' | 'sticky' | 'collision';

export class SoundManager {
  // Separate mute states for SFX and music
  private static sfxMuted: boolean = false;
  private static musicMuted: boolean = false;
  private static readonly SFX_STORAGE_KEY = 'blobo_sfx_muted';
  private static readonly MUSIC_STORAGE_KEY = 'blobo_music_muted';

  // Background music
  private static musicTracks: string[] = [
    'Blobo_Soundtrack.mp3',
    'The Endless Leap.mp3',
    'Pixel Symphony_ Climb of Chaos.mp3',
  ];
  private static currentTrackIndex: number = 0;
  private static musicAudio: HTMLAudioElement | null = null;
  private static readonly BASE_MUSIC_VOLUME = 0.0245; // 30% quieter (0.035 * 0.7)
  private static musicStarted: boolean = false; // Track if music has been started

  // Fall sounds (OhNo audio files)
  private static fallSounds: HTMLAudioElement[] = [];
  private static currentFallSoundIndex: number = 0;
  private static readonly BASE_FALL_SOUND_VOLUME = 0.8125; // 25% louder (0.65 * 1.25)

  /**
   * Sound definitions using ZzFX parameters
   * Format: [volume, randomness, frequency, attack, sustain, release, shape, shapeCurve, vibrato, vibratoDepth, ...]
   */
  private static readonly sounds: Record<SoundType, Parameters<typeof zzfx>> = {
    // Jump - quick chirp upward (25% louder: 0.5 * 1.25 = 0.625)
    jump: [0.625, , 200, 0.01, 0.05, 0.2, 1, 2, , , , , , , , , , 0.6, 0.01] as Parameters<
      typeof zzfx
    >,

    // Land - soft thud (25% louder: 0.4 * 1.25 = 0.5)
    land: [0.5, , 150, 0.01, 0.01, 0.08, 1, 1.5, , , , , , , , , , 0.5] as Parameters<typeof zzfx>,

    // Bounce - springy boing (25% louder: 0.5 * 1.25 = 0.625)
    bounce: [0.625, , 600, 0.02, 0.1, 0.2, 1, 1.8, , , 400, 0.05, , , , , , 0.8] as Parameters<
      typeof zzfx
    >,

    // Slide - continuous swoosh (short) (25% louder: 0.3 * 1.25 = 0.375)
    slide: [0.375, , 100, 0.01, , 0.05, 1, 0.5, , , , , , , , , , 0.3, 0.01] as Parameters<
      typeof zzfx
    >,

    // Sticky - squelchy stick sound (50% quieter than previous: 0.5 * 0.5 = 0.25)
    sticky: [0.25, , 100, 0.05, , 0.1, 4, 0.1, , , , , , , , , , 0.4] as Parameters<typeof zzfx>,

    // Collision - bump/bonk (25% louder: 0.4 * 1.25 = 0.5)
    collision: [0.5, , 250, 0.01, , 0.02, 1, 2, , -10, , , , , , , , 0.5] as Parameters<
      typeof zzfx
    >,
  };

  /**
   * Initialize sound manager, load mute states from localStorage
   */
  static initialize(): void {
    // iOS Performance: Skip audio initialization if disabled for testing
    if (GAME_CONSTANTS.DEBUG.DISABLE_AUDIO) {
      this.sfxMuted = true;
      this.musicMuted = true;
      return;
    }

    const sfxStored = localStorage.getItem(this.SFX_STORAGE_KEY);
    const musicStored = localStorage.getItem(this.MUSIC_STORAGE_KEY);

    // SFX: default to ON (not muted) if no preference stored
    this.sfxMuted = sfxStored === 'true';

    // Music: default to ON (not muted) if no preference stored
    // If musicStored is null (first time), default to not muted (false)
    // If musicStored is 'true', user explicitly turned it off, so mute
    this.musicMuted = musicStored === null ? false : musicStored === 'true';

    this.initializeMusic();
    this.initializeFallSounds();
  }

  /**
   * Initialize fall sounds (7 different fall reaction sounds)
   */
  private static initializeFallSounds(): void {
    // Load all 7 fall sounds from public folder
    const fallSoundFiles = [
      '/OhNo1.mp3',
      '/OhNo2.mp3',
      '/OhNo3.mp3',
      '/OhNo4.mp3',
      '/OhNo5.mp3',
      '/OhNo6.mp3',
      '/OhNo7.mp3',
    ];

    this.fallSounds = fallSoundFiles.map(file => {
      const audio = new Audio(file);
      audio.volume = this.BASE_FALL_SOUND_VOLUME * SettingsManager.getVolumeFall();
      return audio;
    });

    // console.log(`🔊 Loaded ${this.fallSounds.length} fall sounds:`, fallSoundFiles.map(f => f.split('/').pop()).join(', '));
  }

  /**
   * Initialize background music
   */
  private static initializeMusic(): void {
    if (!this.musicAudio) {
      this.musicAudio = new Audio();
      this.musicAudio.volume = this.BASE_MUSIC_VOLUME * SettingsManager.getVolumeMusic();
      this.musicAudio.loop = false; // We'll handle track switching manually

      // When a track ends, play the next one
      this.musicAudio.addEventListener('ended', () => {
        this.playNextTrack();
      });
    }
  }

  /**
   * Start playing background music
   */
  static startMusic(): void {
    if (!this.musicAudio) return;

    this.musicStarted = true;

    if (this.musicMuted) {
      // console.log('Music is muted, not starting playback');
      return;
    }

    // Start with first track
    this.currentTrackIndex = 0;
    this.playCurrentTrack();
  }

  /**
   * Play the current track
   */
  private static playCurrentTrack(): void {
    if (!this.musicAudio || this.musicMuted) return;

    const trackPath = `/${this.musicTracks[this.currentTrackIndex]}`;
    this.musicAudio.src = trackPath;

    // Play with error handling for autoplay restrictions
    const playPromise = this.musicAudio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // console.log('Music autoplay prevented:', error.message);
        // console.log('Music will start after user interaction');
      });
    }
  }

  /**
   * Play the next track in the playlist
   */
  private static playNextTrack(): void {
    this.currentTrackIndex = (this.currentTrackIndex + 1) % this.musicTracks.length;
    this.playCurrentTrack();
  }

  /**
   * Stop background music
   */
  static stopMusic(): void {
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.musicAudio.currentTime = 0;
    }
  }

  /**
   * Resume music if it was stopped
   */
  static resumeMusic(): void {
    if (!this.musicAudio || this.musicMuted) return;

    if (this.musicAudio.paused) {
      const playPromise = this.musicAudio.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // console.log('Music resume prevented:', error.message);
        });
      }
    }
  }

  /**
   * Play a sound effect
   */
  static play(soundType: SoundType): void {
    if (this.sfxMuted) return;

    const sound = this.sounds[soundType];
    if (sound) {
      // Apply volume multiplier from settings
      const volumeMultiplier = SettingsManager.getVolumeSfx();
      const adjustedSound = [...sound] as Parameters<typeof zzfx>;
      adjustedSound[0] = (sound[0] || 0) * volumeMultiplier;
      zzfx(...adjustedSound);
    }

    // Try to start music on first user interaction if it hasn't started yet
    this.tryStartMusicOnInteraction();
  }

  /**
   * Play jump sound with dynamic pitch based on charge amount
   * @param chargePercent - Charge percentage from 0.0 (quick hop) to 1.0 (full jump)
   */
  static playJump(chargePercent: number): void {
    if (this.sfxMuted) return;

    // Clamp charge percent to 0-1 range
    chargePercent = Math.max(0, Math.min(1, chargePercent));

    // Map charge to frequency range:
    // Quick hop (0%)   → 126 Hz (low, subtle tone)
    // Full jump (100%) → 210 Hz (higher, but not too high)
    const minFreq = 126;
    const maxFreq = 210;
    const frequency = minFreq + (maxFreq - minFreq) * chargePercent;

    // Apply volume multiplier from settings
    const volumeMultiplier = SettingsManager.getVolumeSfx();
    const baseVolume = 0.625;

    // Create dynamic jump sound with calculated frequency
    // Base sound parameters similar to the static jump sound, but with dynamic frequency
    const dynamicJumpSound: Parameters<typeof zzfx> = [
      baseVolume * volumeMultiplier, // volume with settings multiplier
      0, // randomness (none)
      frequency, // dynamic frequency based on charge
      0.01, // attack
      0.05, // sustain
      0.2, // release
      1, // shape
      2, // shapeCurve
      // vibrato
      // vibratoDepth
      //
      //
      //
      //
      //
      //
      //
      ,
      ,
      ,
      ,
      ,
      ,
      ,
      ,
      ,
      0.6, //
      0.01, //
    ];

    zzfx(...dynamicJumpSound);

    // Try to start music on first user interaction if it hasn't started yet
    this.tryStartMusicOnInteraction();
  }

  /**
   * Play fall sound (cycles through all 7 fall sounds)
   * Ensures only one fall sound plays at a time
   */
  static playFallSound(): void {
    if (this.sfxMuted) return;
    if (this.fallSounds.length === 0) return;

    // Stop all currently playing fall sounds to ensure only one plays at a time
    this.fallSounds.forEach(sound => {
      if (!sound.paused) {
        sound.pause();
        sound.currentTime = 0;
      }
    });

    // Get the current fall sound
    const fallSound = this.fallSounds[this.currentFallSoundIndex];
    if (!fallSound) return;

    // Reset to beginning and play
    fallSound.currentTime = 0;
    const playPromise = fallSound.play();

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // console.log('Fall sound play prevented:', error.message);
      });
    }

    // Alternate to the next sound for next time
    this.currentFallSoundIndex = (this.currentFallSoundIndex + 1) % this.fallSounds.length;
    // console.log(`🔊 Playing fall sound ${currentIndex + 1}/${this.fallSounds.length}, next will be ${this.currentFallSoundIndex + 1}/${this.fallSounds.length}`);

    // Try to start music on first user interaction if it hasn't started yet
    this.tryStartMusicOnInteraction();
  }

  /**
   * Try to start music on user interaction (helps with autoplay restrictions)
   */
  private static tryStartMusicOnInteraction(): void {
    if (!this.musicStarted || (this.musicAudio && this.musicAudio.paused && !this.musicMuted)) {
      // Attempt to start/resume music since we now have user interaction
      if (!this.musicStarted) {
        this.startMusic();
      } else if (!this.musicMuted) {
        this.resumeMusic();
      }
    }
  }

  /**
   * Toggle SFX mute on/off
   */
  static toggleSfxMute(): boolean {
    this.sfxMuted = !this.sfxMuted;
    localStorage.setItem(this.SFX_STORAGE_KEY, String(this.sfxMuted));
    return this.sfxMuted;
  }

  /**
   * Toggle music mute on/off
   */
  static toggleMusicMute(): boolean {
    this.musicMuted = !this.musicMuted;
    localStorage.setItem(this.MUSIC_STORAGE_KEY, String(this.musicMuted));

    // Handle music playback
    if (this.musicAudio) {
      if (this.musicMuted) {
        this.musicAudio.pause();
      } else {
        // If music was started, resume it
        if (this.musicStarted) {
          this.resumeMusic();
        }
      }
    }

    return this.musicMuted;
  }

  /**
   * Get current SFX mute state
   */
  static isSfxMuted(): boolean {
    return this.sfxMuted;
  }

  /**
   * Get current music mute state
   */
  static isMusicMuted(): boolean {
    return this.musicMuted;
  }

  /**
   * Set SFX mute state
   */
  static setSfxMuted(muted: boolean): void {
    this.sfxMuted = muted;
    localStorage.setItem(this.SFX_STORAGE_KEY, String(muted));
  }

  /**
   * Set music mute state
   */
  static setMusicMuted(muted: boolean): void {
    this.musicMuted = muted;
    localStorage.setItem(this.MUSIC_STORAGE_KEY, String(muted));

    // Handle music playback
    if (this.musicAudio) {
      if (this.musicMuted) {
        this.musicAudio.pause();
      } else {
        // If music was started, resume it
        if (this.musicStarted) {
          this.resumeMusic();
        }
      }
    }
  }

  /**
   * Update fall sounds volume based on current settings
   */
  static updateFallSoundsVolume(): void {
    const volume = this.BASE_FALL_SOUND_VOLUME * SettingsManager.getVolumeFall();
    this.fallSounds.forEach(sound => {
      sound.volume = volume;
    });
  }

  /**
   * Update music volume based on current settings
   */
  static updateMusicVolume(): void {
    if (this.musicAudio) {
      this.musicAudio.volume = this.BASE_MUSIC_VOLUME * SettingsManager.getVolumeMusic();
    }
  }

  /**
   * Update all volumes based on current settings
   * Call this when volume settings change
   */
  static updateAllVolumes(): void {
    this.updateFallSoundsVolume();
    this.updateMusicVolume();
    // SFX volumes are applied per-sound, so no update needed
  }
}
